import React from 'react';

/**
 * Catalog of the applications that are actually DEPLOYED on the Vultr host
 * (condoSystemNew → Condo Manager, pestpulse-pro, projectEHS2). Used by the
 * `/tileapps` launcher and the `/products` landing page.
 *
 * All artwork is inline SVG and all links resolve from the current host, so
 * these surfaces make ZERO external network requests (no Google Fonts, no
 * clearbit logos) — which is what previously left `/tileapps` "loading forever".
 */

export type AppLink =
  | { external: true; port: number }   // sibling app on the same host, other port
  | { external: false; path: string }; // in-SPA route

export interface DeployedApp {
  id: string;
  name: string;
  tagline: string;
  description: string;
  features: string[];
  accent: string;
  accentDark: string;
  link: AppLink;
  Art: React.FC;
}

/**
 * Host where the sibling apps live. They run on the server's IP ports (only the
 * condo/ServiceBuds site is proxied through the servicebuds.com domain), so
 * external links point at the IP:port directly. Update if the server IP changes.
 */
export const APP_HOST = 'http://155.138.135.108';

/** Build the href for an app link. Internal → in-SPA path; external → server IP:port. */
export function resolveHref(link: AppLink): string {
  if (!link.external) return link.path;
  return `${APP_HOST}:${link.port}`;
}

// ── Cover artwork (inline SVG, white-on-gradient) ─────────────────────────────

const CondoArt: React.FC = () => (
  <svg width="78" height="78" viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <rect x="8" y="18" width="18" height="38" rx="2" fill="rgba(255,255,255,.20)" stroke="#fff" strokeWidth="2" />
    <rect x="28" y="8" width="26" height="48" rx="2" fill="rgba(255,255,255,.14)" stroke="#fff" strokeWidth="2" />
    <g fill="#fff">
      <rect x="12" y="24" width="4" height="4" rx="1" />
      <rect x="18" y="24" width="4" height="4" rx="1" />
      <rect x="12" y="32" width="4" height="4" rx="1" />
      <rect x="18" y="32" width="4" height="4" rx="1" />
      <rect x="12" y="40" width="4" height="4" rx="1" />
      <rect x="18" y="40" width="4" height="4" rx="1" />
      <rect x="33" y="14" width="5" height="5" rx="1" />
      <rect x="41" y="14" width="5" height="5" rx="1" />
      <rect x="33" y="24" width="5" height="5" rx="1" />
      <rect x="41" y="24" width="5" height="5" rx="1" />
      <rect x="33" y="34" width="5" height="5" rx="1" />
      <rect x="41" y="34" width="5" height="5" rx="1" />
    </g>
    <rect x="5" y="55" width="54" height="4" rx="2" fill="#fff" />
  </svg>
);

const PestArt: React.FC = () => (
  <svg width="78" height="78" viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <path d="M32 6l20 7v13c0 12.5-8.4 21.9-20 26C20.4 47.9 12 38.5 12 26V13l20-7z"
      fill="rgba(255,255,255,.16)" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
    <circle cx="32" cy="28" r="10" fill="none" stroke="#fff" strokeWidth="2" />
    <circle cx="32" cy="28" r="4" fill="#fff" />
    <path d="M32 12v6M32 38v6M16 28h6M42 28h6" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SafetyArt: React.FC = () => (
  <svg width="78" height="78" viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <path d="M12 40c0-11 9-20 20-20s20 9 20 20" fill="rgba(255,255,255,.18)" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    <rect x="27" y="12" width="10" height="10" rx="2" fill="#fff" />
    <rect x="8" y="40" width="48" height="7" rx="3" fill="#fff" />
    <path d="M26 33l4 4 8-8" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const NexusArt: React.FC = () => (
  <svg width="78" height="78" viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <rect x="14" y="8" width="36" height="48" rx="4" fill="rgba(255,255,255,.16)" stroke="#fff" strokeWidth="2" />
    <path d="M24 6h16a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H24a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" fill="#fff" />
    <path d="M20 34h5l3-7 4 13 3-6h9" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <g stroke="#fff" strokeWidth="2" strokeLinecap="round">
      <path d="M20 44h16M20 49h11" />
    </g>
    <circle cx="32" cy="21" r="4.5" fill="none" stroke="#fff" strokeWidth="2" />
    <path d="M32 19v4M30 21h4" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const EduWizArt: React.FC = () => (
  <svg width="78" height="78" viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <path d="M32 10 L56 20 L32 30 L8 20 Z" fill="rgba(255,255,255,.20)" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
    <path d="M18 25 v9 c0 4 6 7 14 7 s14 -3 14 -7 v-9" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    <path d="M56 20 v14" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    <circle cx="56" cy="36" r="2.5" fill="#fff" />
    <path d="M25 45 l-7 6 l7 6 M39 45 l7 6 l-7 6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="M35 44 l-6 14" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

// ── The deployed apps ─────────────────────────────────────────────────────────

export const DEPLOYED_APPS: DeployedApp[] = [
  {
    id: 'condo',
    name: 'Condo Manager',
    tagline: 'Property & community operations',
    description:
      'Multi-tenant condominium management — fees & payments, maintenance workflows, amenity bookings, governance, and resident portals.',
    features: ['Fees & payments', 'Maintenance', 'Amenity bookings', 'Governance'],
    accent: '#2563EB',
    accentDark: '#1E3A8A',
    link: { external: false, path: '/condo-app/' },
    Art: CondoArt,
  },
  {
    id: 'pestpulse',
    name: 'PestPulse Pro',
    tagline: 'Pest-control operating system',
    description:
      'The operating system for pest-control operators — CRM & scheduling, compliance logs, smart IoT traps, and AI vision monitoring.',
    features: ['CRM & scheduling', 'Compliance logs', 'Smart IoT', 'AI vision'],
    accent: '#0E9F6E',
    accentDark: '#065F46',
    link: { external: true, port: 3000 },
    Art: PestArt,
  },
  {
    id: 'ehs',
    name: 'EHS Guardian',
    tagline: 'Environment, health & safety',
    description:
      'Command-center for workplace safety — incident tracking, corrective actions, projects, training records, and custom reports.',
    features: ['Incidents', 'Corrective actions', 'Training', 'Reports'],
    accent: '#E8730C',
    accentDark: '#9A3412',
    link: { external: true, port: 8082 },
    Art: SafetyArt,
  },
  {
    id: 'nexus',
    name: 'Nexus Health',
    tagline: 'Electronic health records',
    description:
      'A modern EHR for clinics — patient records, scheduling, a clinical dashboard, and lab/observation tracking in one workspace.',
    features: ['Patient records', 'Scheduling', 'Clinical dashboard', 'Lab results'],
    accent: '#0EA5E9',
    accentDark: '#0C4A6E',
    link: { external: true, port: 8084 },
    Art: NexusArt,
  },
  {
    id: 'eduwiz',
    name: 'EduWiz',
    tagline: 'Coding education platform',
    description:
      'A learn-to-code platform for schools — courses and learning paths, an in-browser code editor, quizzes, and student progress tracking.',
    features: ['Courses & paths', 'Code editor', 'Quizzes', 'Progress tracking'],
    accent: '#7C3AED',
    accentDark: '#4C1D95',
    link: { external: true, port: 8083 },
    Art: EduWizArt,
  },
];
